import React from "react";
import { Route } from "react-router";
import MedicationStatus from "main/MedicationFlow/MedicationStatus";
import MedicationSearchContainer from "main/MedicationFlow/MedicationSearch/MedicationSearchContainer";
import MedicationPosology from "main/MedicationFlow/MedicationPosology";
import MedicationPosologyFree from "main/MedicationFlow/MedicationPosologyFree";
import MedicationPosologySimple from "main/MedicationFlow/MedicationPosologySimple";
import MedicationPeriod from "main/MedicationFlow/MedicationPeriod";
import SwipeableRoutes, {
  SwipeableRouteChildrenProps,
} from "react-swipeable-routes";
import MedicationDone from "main/MedicationFlow/MedicationDone";
import { ROUTE_MEDICATION } from "routes.global";
import { MedicationEntryType } from "model/medication-entry.model";
import { TemporalityEnum } from "common/enums/TemporalityEnum";
import { StatusTypeEnum } from "common/enums/StatusTypeEnum";
import { PosologyTypeEnum } from "common/enums/PosologyTypeEnum";
import { PeriodEnum } from "common/enums/PeriodEnum";
import { newMedication } from "store/actions/medication.actions";
import { useDispatch, useSelector } from "react-redux";
import { selectPatient } from "store/reducers/patient.reducer";
import { selectUser } from "store/reducers/user-context.reducer";

interface Props {
  activeMedicationEntry?: MedicationEntryType;
  setFromMedicationSearch: any;
  selectedTemporality?: TemporalityEnum;
  setSelectedTemporality: any;
  selectedStatus?: StatusTypeEnum;
  setSelectedStatus: any;
  selectedPosologyType: PosologyTypeEnum;
  setSelectedPosologyType: any;
  freePosology: string;
  setFreePosology: any;
  selectionPeriod: {
    number: number;
    unit: PeriodEnum;
  };
  setSelectionPeriod: any;
  isVOS: any;
  onEdit: any;
}

const MedicationRoutes = ({
  activeMedicationEntry,
  setFromMedicationSearch,
  selectedTemporality,
  setSelectedTemporality,
  selectedStatus,
  setSelectedStatus,
  selectedPosologyType,
  setSelectedPosologyType,
  freePosology,
  setFreePosology,
  selectionPeriod,
  setSelectionPeriod,
  isVOS,
  onEdit,
}: Props) => {
  const dispatch = useDispatch();
  const patient = useSelector(selectPatient);
  const user = useSelector(selectUser);
  const baseLocation = ROUTE_MEDICATION;
  const simplePosologyImplemented = false;

  const validateNotEmptyMessage = (freePosology: string) => {
    if (selectedPosologyType !== PosologyTypeEnum.FREE_TEXT) {
      return true;
    }
    return freePosology !== "";
  };

  const routes = [];

  const medicationSearchRoute = () => (
    <Route
      key="r1"
      path={baseLocation + "/search"}
      render={() => (
        <MedicationSearchContainer
          key="medicationFlowSearch"
          onSelectMedication={(entry) => {
            setFromMedicationSearch(true);
            dispatch(newMedication(patient.id, user.id, entry));
          }}
        />
      )}
    />
  );

  const medicationStatusRoute = () => (
    <Route
      key="r2"
      path={baseLocation + "/status"}
      render={() => (
        <MedicationStatus
          key="medicationFlowStatus"
          selectedTemporality={selectedTemporality}
          setSelectedTemporality={setSelectedTemporality}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          activeMedicationEntry={activeMedicationEntry}
        />
      )}
    />
  );

  const medicationPosologyRoute = () => (
    <Route
      key="r3"
      path={baseLocation + "/posology"}
      render={() => (
        <MedicationPosology
          key="medicationFlowPosology"
          selectedPosologyType={selectedPosologyType}
          setSelectedPosologyType={setSelectedPosologyType}
        />
      )}
    />
  );

  const medicationFreeTextRoute = () => (
    <Route
      key="r4"
      path={baseLocation + "/free"}
      render={() => (
        <MedicationPosologyFree
          key="medicationFlowFree"
          freePosology={freePosology}
          setFreePosology={setFreePosology}
          isValid={validateNotEmptyMessage(freePosology)}
        />
      )}
    />
  );

  const medicationSimplePosologyRoute = () => (
    <Route
      key="r5"
      path={baseLocation + "/simple"}
      render={() => <MedicationPosologySimple key="medicationFlowSimple" />}
    />
  );

  const medicationPeriodRoute = () => (
    <Route
      key="r6"
      path={baseLocation + "/period"}
      render={() => (
        <MedicationPeriod
          key="medicationFlowPeriod"
          selectionPeriod={selectionPeriod}
          setSelectionPeriod={setSelectionPeriod}
        />
      )}
    />
  );

  const medicationDoneRoute = () => (
    <Route
      key="k7"
      path={baseLocation + "/done"}
      component={({ match }: SwipeableRouteChildrenProps) => (
        <MedicationDone
          key="medicationFlowDone"
          inView={match.type === "full"}
          onInView={onEdit}
          isValid={validateNotEmptyMessage(freePosology)}
        />
      )}
    />
  );

  if (!activeMedicationEntry || activeMedicationEntry.id === undefined) {
    routes.push(medicationSearchRoute());
  }

  if (activeMedicationEntry) {
    routes.push(medicationStatusRoute());
    if (selectedStatus !== StatusTypeEnum.NOT_RELEVANT) {
      simplePosologyImplemented && routes.push(medicationPosologyRoute());
      if (selectedPosologyType === PosologyTypeEnum.FREE_TEXT) {
        routes.push(medicationFreeTextRoute());
      }
      if (
        simplePosologyImplemented &&
        selectedPosologyType === PosologyTypeEnum.SIMPLE
      ) {
        routes.push(medicationSimplePosologyRoute());
      }
      if (isVOS(activeMedicationEntry.freeTextContent)) {
        routes.push(medicationPeriodRoute());
      }
      routes.push(medicationDoneRoute());
    }
  }
  return (
    <SwipeableRoutes
      containerStyle={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexGrow: 1,
      }}
      style={{
        width: "100%",
        display: "flex",
        height: "100%",
        overflow: "hidden",
      }}
      enableMouseEvents
      replace
      ignoreNativeScroll={true}
    >
      {routes}
    </SwipeableRoutes>
  );
};

export default MedicationRoutes;
